import { ChartLegendWrapper, Barchart as CoreUIBarChart } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useHistChartParams } from '../../../hooks/useHistChartParams';
import { legendInstancesOf } from '../../../portal/instanceMarker';
import { ChartBody, ChartCard, ChartHeaderActions } from '../../chat-messages/ChartWrapper';
import type { AIToolComponent } from '../../chat-messages/GuardianMarkdown';
import { ResolveInstanceMarkers } from '../../ResolveInstanceMarkers';
import { NoData } from '../NoData';
import { ChartLegendPanel } from '../shared/ChartLegendPanel';
import { buildCategoryTable } from '../shared/chartExportTables';

type HistChartProps = {
  title: string;
  yAxisTitle: string;
  xAxisTitle: string;
  x: number[];
  series: { label: string; y: number[] }[];
};

const BAR_CHART_TYPE = { type: 'category', gap: 0 } as const;

type HistChartViewProps = HistChartProps & { instances: ReturnType<typeof legendInstancesOf> };

const HistChartView: React.FC<HistChartViewProps> = ({ title, yAxisTitle, xAxisTitle, x, series, instances }) => {
  const { bars, colorSet } = useHistChartParams({ x, series });

  if (x.length === 0 || !series.some((serie) => serie.y.length > 0)) {
    return (
      <ChartCard>
        <NoData title={title} />
      </ChartCard>
    );
  }

  return (
    <ChartCard
      title={title}
      buildExportTable={() =>
        buildCategoryTable({
          name: title,
          categoryColumn: xAxisTitle,
          categories: x,
          series: series.map((serie) => ({ label: serie.label, values: serie.y })),
        })
      }
    >
      {(view) => (
        <ChartLegendWrapper colorSet={colorSet}>
          <ChartBody legend={<ChartLegendPanel instances={instances} />}>
            <CoreUIBarChart
              type={BAR_CHART_TYPE}
              stacked
              title={title}
              rightTitle={<ChartHeaderActions actions={view.actions} caption={yAxisTitle} />}
              bars={bars}
              height={view.chartHeight}
            />
          </ChartBody>
        </ChartLegendWrapper>
      )}
    </ChartCard>
  );
};

export const HistChart: AIToolComponent<HistChartProps> = ({ params }) => (
  <ResolveInstanceMarkers
    value={params.series}
    render={(series) => (
      <HistChartView {...params} series={series} instances={legendInstancesOf(params.series, series)} />
    )}
  />
);

HistChart.description = 'Displays a histogram chart to display categorized data points.';

HistChart.schema = {
  type: 'object',
  properties: {
    title: { type: 'string' },
    yAxisTitle: { type: 'string' },
    xAxisTitle: { type: 'string' },
    x: { type: 'array', items: { type: 'number' }, minItems: 2 },
    series: {
      type: 'array',
      minItems: 1,
      items: {
        type: 'object',
        properties: {
          label: { type: 'string' },
          y: { type: 'array', items: { type: 'number' }, minItems: 2 },
        },
        required: ['label', 'y'],
      },
    },
  },
  required: ['title', 'yAxisTitle', 'xAxisTitle', 'x', 'series'],
};

HistChart.displayInPanel = true;
